<template>
  <div 
    v-if="showBanner" 
    class="cookie-consent-banner fixed bottom-0 left-0 right-0 z-50 bg-gray-900 border-t border-gray-700 shadow-lg"
  >
    <div class="container mx-auto px-4 py-4">
      <div class="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
        <!-- Cookie Info -->
        <div class="flex-1">
          <div class="flex items-start gap-3">
            <div class="flex-shrink-0 mt-1">
              <svg class="w-6 h-6 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
            </div>
            <div class="text-sm text-gray-300">
              <p class="mb-2">
                <strong class="text-white">We use cookies to enhance your experience</strong>
              </p>
              <p>
                This website uses cookies and similar technologies to provide you with a personalized gaming experience, analyze website traffic, and display relevant advertisements through Google AdSense. 
                <router-link to="/privacy" class="text-blue-400 hover:text-blue-300 underline">Learn more in our Privacy Policy</router-link>
              </p>
            </div>
          </div>
        </div>

        <!-- Action Buttons -->
        <div class="flex flex-col sm:flex-row gap-3 w-full lg:w-auto">
          <!-- Settings Button -->
          <button
            @click="showSettings = !showSettings"
            class="px-4 py-2 text-sm font-medium text-gray-300 hover:text-white border border-gray-600 hover:border-gray-500 rounded-lg transition-colors duration-200 w-full sm:w-auto"
          >
            <svg class="w-4 h-4 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"></path>
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
            </svg>
            Cookie Settings
          </button>
          
          <!-- Accept Essential -->
          <button
            @click="acceptEssential"
            class="px-4 py-2 text-sm font-medium text-gray-900 bg-gray-300 hover:bg-gray-200 rounded-lg transition-colors duration-200 w-full sm:w-auto"
          >
            Essential Only
          </button>
          
          <!-- Accept All -->
          <button
            @click="acceptAll"
            class="px-6 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors duration-200 w-full sm:w-auto"
          >
            Accept All
          </button>
        </div>
      </div>

      <!-- Cookie Settings Panel -->
      <div v-if="showSettings" class="mt-6 pt-6 border-t border-gray-700">
        <h3 class="text-lg font-semibold text-white mb-4">Cookie Preferences</h3>
        
        <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
          <!-- Essential Cookies -->
          <div class="cookie-category">
            <div class="flex items-center justify-between mb-2">
              <h4 class="font-medium text-white">Essential Cookies</h4>
              <div class="toggle-switch disabled">
                <input type="checkbox" checked disabled class="sr-only">
                <div class="toggle-bg bg-blue-600"></div>
                <div class="toggle-dot"></div>
              </div>
            </div>
            <p class="text-sm text-gray-400">
              Required for basic website functionality. These cannot be disabled.
            </p>
          </div>

          <!-- Analytics Cookies -->
          <div class="cookie-category">
            <div class="flex items-center justify-between mb-2">
              <h4 class="font-medium text-white">Analytics Cookies</h4>
              <div class="toggle-switch" @click="toggleAnalytics">
                <input type="checkbox" v-model="preferences.analytics" class="sr-only">
                <div class="toggle-bg" :class="preferences.analytics ? 'bg-blue-600' : 'bg-gray-600'"></div>
                <div class="toggle-dot" :class="preferences.analytics ? 'translate-x-6' : 'translate-x-1'"></div>
              </div>
            </div>
            <p class="text-sm text-gray-400">
              Help us understand how visitors use our website (Google Analytics).
            </p>
          </div>

          <!-- Advertising Cookies -->
          <div class="cookie-category">
            <div class="flex items-center justify-between mb-2">
              <h4 class="font-medium text-white">Advertising Cookies</h4>
              <div class="toggle-switch" @click="toggleAdvertising">
                <input type="checkbox" v-model="preferences.advertising" class="sr-only">
                <div class="toggle-bg" :class="preferences.advertising ? 'bg-blue-600' : 'bg-gray-600'"></div>
                <div class="toggle-dot" :class="preferences.advertising ? 'translate-x-6' : 'translate-x-1'"></div>
              </div>
            </div>
            <p class="text-sm text-gray-400">
              Used to deliver relevant advertisements (Google AdSense).
            </p>
          </div>
        </div>

        <div class="flex justify-end mt-6 gap-3">
          <button
            @click="savePreferences"
            class="px-6 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors duration-200"
          >
            Save Preferences
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, onMounted } from 'vue'

export default {
  name: 'CookieConsent',
  setup() {
    const showBanner = ref(false)
    const showSettings = ref(false)
    
    const preferences = reactive({
      analytics: true,
      advertising: true
    })

    // Check if user has already made a choice
    const checkConsentStatus = () => {
      const consent = localStorage.getItem('cookieConsent')
      if (!consent) {
        showBanner.value = true
      } else {
        const savedPreferences = JSON.parse(consent)
        preferences.analytics = savedPreferences.analytics
        preferences.advertising = savedPreferences.advertising
        applyPreferences(savedPreferences)
      }
    }

    // Apply cookie preferences
    const applyPreferences = (prefs) => {
      // Handle Analytics
      if (prefs.analytics && window.gtag) {
        window.gtag('consent', 'update', {
          'analytics_storage': 'granted'
        })
      } else if (window.gtag) {
        window.gtag('consent', 'update', {
          'analytics_storage': 'denied'
        })
      }

      // Handle Advertising
      if (prefs.advertising && window.gtag) {
        window.gtag('consent', 'update', {
          'ad_storage': 'granted',
          'ad_user_data': 'granted',
          'ad_personalization': 'granted'
        })
      } else if (window.gtag) {
        window.gtag('consent', 'update', {
          'ad_storage': 'denied',
          'ad_user_data': 'denied',
          'ad_personalization': 'denied'
        })
      }

      // Emit event for parent components
      window.dispatchEvent(new CustomEvent('cookiePreferencesUpdated', { 
        detail: prefs 
      }))
    }

    // Accept all cookies
    const acceptAll = () => {
      const consent = {
        essential: true,
        analytics: true,
        advertising: true,
        timestamp: Date.now()
      }
      
      localStorage.setItem('cookieConsent', JSON.stringify(consent))
      applyPreferences(consent)
      showBanner.value = false
      
      // Track consent event
      if (window.gtag) {
        window.gtag('event', 'cookie_consent', {
          event_category: 'Privacy',
          event_label: 'Accept All'
        })
      }
    }

    // Accept essential cookies only
    const acceptEssential = () => {
      const consent = {
        essential: true,
        analytics: false,
        advertising: false,
        timestamp: Date.now()
      }
      
      localStorage.setItem('cookieConsent', JSON.stringify(consent))
      applyPreferences(consent)
      showBanner.value = false
      
      // Track consent event
      if (window.gtag) {
        window.gtag('event', 'cookie_consent', {
          event_category: 'Privacy',
          event_label: 'Essential Only'
        })
      }
    }

    // Save custom preferences
    const savePreferences = () => {
      const consent = {
        essential: true,
        analytics: preferences.analytics,
        advertising: preferences.advertising,
        timestamp: Date.now()
      }
      
      localStorage.setItem('cookieConsent', JSON.stringify(consent))
      applyPreferences(consent)
      showBanner.value = false
      showSettings.value = false
      
      // Track consent event
      if (window.gtag) {
        window.gtag('event', 'cookie_consent', {
          event_category: 'Privacy',
          event_label: 'Custom Preferences'
        })
      }
    }

    // Toggle functions
    const toggleAnalytics = () => {
      preferences.analytics = !preferences.analytics
    }

    const toggleAdvertising = () => {
      preferences.advertising = !preferences.advertising
    }

    // Initialize on mount
    onMounted(() => {
      // Set default consent to denied until user makes a choice
      if (window.gtag) {
        window.gtag('consent', 'default', {
          'ad_storage': 'denied',
          'ad_user_data': 'denied',
          'ad_personalization': 'denied',
          'analytics_storage': 'denied'
        })
      }
      
      setTimeout(checkConsentStatus, 1000) // Delay to ensure page is loaded
    })

    return {
      showBanner,
      showSettings,
      preferences,
      acceptAll,
      acceptEssential,
      savePreferences,
      toggleAnalytics,
      toggleAdvertising
    }
  }
}
</script>

<style scoped>
.cookie-consent-banner {
  backdrop-filter: blur(10px);
  background-color: rgba(17, 24, 39, 0.95);
}

.cookie-category {
  padding: 1rem;
  border: 1px solid #374151;
  border-radius: 0.5rem;
  background-color: #1f2937;
}

.toggle-switch {
  position: relative;
  width: 3rem;
  height: 1.5rem;
  cursor: pointer;
}

.toggle-switch.disabled {
  cursor: not-allowed;
  opacity: 0.7;
}

.toggle-bg {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border-radius: 0.75rem;
  transition: background-color 0.2s ease-in-out;
}

.toggle-dot {
  position: absolute;
  top: 0.125rem;
  width: 1.25rem;
  height: 1.25rem;
  background-color: white;
  border-radius: 50%;
  transition: transform 0.2s ease-in-out;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

@media (max-width: 640px) {
  .cookie-consent-banner {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
  }
  
  .cookie-category {
    padding: 0.75rem;
  }
}

/* Animation for banner appearance */
.cookie-consent-banner {
  animation: slideUp 0.3s ease-out;
}

@keyframes slideUp {
  from {
    transform: translateY(100%);
  }
  to {
    transform: translateY(0);
  }
}
</style>
